import { Add } from '@mui/icons-material';
import {
  Box, Button, Checkbox, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment, TextField, Tooltip, Typography
} from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { postReviewerForm } from '../../../../../api/facility';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import FormDropdown from '../../../../../reusable-components/form/FormDropdown';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import BudgetMenu from './BudgetMenu';
import CategoryPicker from './CategoryPicker';

MultisetReviewers.propTypes = {
  selectedRow: PropTypes.object,
  value: PropTypes.object,
  setValue: PropTypes.func,
  categories: PropTypes.array,
  originalValue: PropTypes.object,
  userData: PropTypes.array,
  handleGetData: PropTypes.func,
};
export default function MultisetReviewers({ selectedRow, value,
  setValue, originalValue,
  handleGetData, categories, userData
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  }

  const handleClose = () => {
    setValue(originalValue);
    setOpen(false);
  }

  const handleRemoveAll = async (userToRemove) => {
    // Remove from facility-level reviewers
    if (value.reviewers.some((r) => r?.email === userToRemove?.email)) {
      setValue((prevValue) => {
        const newReviewers = prevValue.reviewers.filter((reviewer) => reviewer.email !== userToRemove.email);
        return { ...prevValue, reviewers: newReviewers };
      });
    }
    // Remove from each user's reviewers
    setValue((prevValue) => {
      const newUsers = prevValue.users.map((u) => ({
        ...u,
        reviewers: u.reviewers.filter((r) => r.email !== userToRemove.email),
      }));
      return { ...prevValue, users: newUsers };
    });
  };


  const handleAddFacLevel = () => {
    setValue(prevValue => {
      const newUsers = prevValue.users.map(user => ({
        ...user,
        reviewers: [...user.reviewers, { categories }]
      }));
      const newReviewers = [...prevValue.reviewers, { categories }];
      return { ...prevValue, reviewers: newReviewers, users: newUsers };
    });
  };

  const handleEditFacLevelSave = async () => {
    setLoading(true)
    try {
      const response = await postReviewerForm(value, selectedRow.id);
      if (response.data.error) {
        enqueueSnackbar(`Error: ${response.data.error}`, {
          variant: 'error',
        });
      } else if (response.status === 200) {
        enqueueSnackbar(`Successfully updated facility reviewers`, { variant: 'success', });
      } else
        enqueueSnackbar(`Error: Could not update reviewers`, { variant: 'error', });
      handleGetData();
      setOpen(false);
    }
    catch (error) {
      enqueueSnackbar(`Error: Could not update reviewers`, { variant: 'error', });
    }
    setLoading(false);
  };

  const setFacilityLevelField = (newValue, index, updateField) => {
    setValue((prevValue) => {
      prevValue?.users.forEach((user) => {
        const userReviewerIndex = user.reviewers
          ?.map((u) => u?.email)
          ?.findIndex((v) => v === prevValue?.reviewers[index].email);
        if (userReviewerIndex > -1) {
          const revs = user.reviewers;
          revs[userReviewerIndex] = { ...revs[userReviewerIndex], [updateField]: newValue };
        }
      });
      const newReviewers = [...prevValue.reviewers];
      const oldUser = newReviewers[index];
      const newReviewer = {
        ...oldUser, [updateField]: newValue
      };
      newReviewers[index] = newReviewer;
      return { ...prevValue, reviewers: newReviewers };
    });
  }

  const disableSaveAllMessage = useMemo(() =>
    _.isEqual(value.reviewers, originalValue.reviewers) ? "No changes were made"
      : !value.reviewers?.every(reviewer => reviewer?.email && reviewer.categories && reviewer.categories.length > 0) ?
        "Missing a required field"
        : null
    , [value, originalValue.reviewers]);

  return (
    <>
      <Button
        variant="contained"
        sx={{ mr: 1 }}
        size="small"
        color="secondary"
        onClick={handleOpen}
      >
        click here to add/update multiple reviewers
      </Button>
      <Dialog open={open} maxWidth="lg"
        PaperProps={{ sx: { width: 1000, maxWidth: '100%' } }} >
        <DialogTitle sx={{ color: "info.main" }}>
          Add/Update Multiple Reviewers
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {value?.reviewers?.length > 0 && value?.reviewers?.map((reviewer, reviewerIndex) =>
              <Grid item xs={4} key={reviewer.id}>
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#fafaff", maxWidth: 350 }}>
                  <Grid container direction="row">
                    <Grid item xs={10}>
                      <FormDropdown
                        disableClearable
                        fullWidth
                        value={value?.reviewers[reviewerIndex]}
                        onChange={(_, newValue) => {
                          setFacilityLevelField(newValue.email, reviewerIndex, "email");
                          setFacilityLevelField(newValue.orderReviewerId, reviewerIndex, "id");
                          setFacilityLevelField(newValue.name, reviewerIndex, "name");
                        }}
                        options={userData}
                        isOptionEqualToValue={(option, value) => option?.email === value?.email}
                        getOptionLabel={(option) => option?.name ? `${option?.name} - ${option?.email}` : ''}
                        renderInput={(params) => <TextField {...params} label="Search or select" />}
                      />
                    </Grid>
                    <ConfirmDialog
                      icon="delete-sweep"
                      title="Delete"
                      color="error"
                      content={`Deleting a reviewer from the facility completely will delete their permission to edit orders`}
                      actionButton="Delete"
                      action={() => handleRemoveAll(reviewer)}
                    />
                  </Grid>
                  <Grid item container direction="column" alignItems="flex-start" sx={{ ml: 0.5 }}>
                    <Grid item container direction="row" alignItems="center" sx={{ mt: 1 }}>
                      <Checkbox color="secondary" size="small" sx={{ py: 0 }}
                        checked={!!value.reviewers[reviewerIndex].categories.length}
                        onChange={(event) => {
                          const newValue = event.target.checked ? categories : [];
                          setFacilityLevelField(newValue, reviewerIndex, "categories");
                        }}
                      />
                      <CategoryPicker
                        value={value.reviewers[reviewerIndex].categories}
                        onChange={(newValue) => setFacilityLevelField(newValue, reviewerIndex, "categories")} categories={categories} />
                    </Grid>
                    <Grid item container direction="row" alignItems="center" sx={{ mt: 1 }}>
                      <Checkbox color="secondary" size="small" sx={{ py: 0 }}
                        checked={!!value.reviewers[reviewerIndex].minimum}
                        onChange={(event) => {
                          const newValue = event.target.checked ? "0" : "";
                          setFacilityLevelField(newValue, reviewerIndex, "minimum");
                        }}
                      />
                      <Typography variant="caption">
                        Review if over
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0 }}
                        variant="standard"
                        label=""
                        sx={{ maxWidth: 70, ml: 3 }}
                        InputLabelProps={{ style: { fontSize: 12, lineHeight: 3, p: 0 } }} // font size of input label
                        InputProps={{
                          sx: { fontSize: 12, p: 0 },
                          startAdornment: (
                            <InputAdornment size="small" position="start">
                              <Typography variant="caption">$</Typography>
                            </InputAdornment>
                          )
                        }}
                        value={value.reviewers[reviewerIndex].minimum}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          setFacilityLevelField(newValue, reviewerIndex, "minimum");
                        }}
                      />
                    </Grid>
                    <Grid item container direction="row" alignItems="center" sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        <Checkbox color="secondary" size="small" sx={{ py: 0 }}
                          checked={!!value.reviewers[reviewerIndex].budget}
                          onChange={(event) => {
                            const newValue = event.target.checked ? "monthly" : undefined;
                            setFacilityLevelField(newValue, reviewerIndex, "budget");
                          }}
                        />
                        <BudgetMenu menuElements={["monthly", "weekly"]} value={value.reviewers[reviewerIndex].budget}
                          onChange={(newValue) => setFacilityLevelField(newValue, reviewerIndex, "budget")} />
                      </Typography>
                    </Grid>
                    <Grid item sx={{ mt: 1 }}>
                      <Checkbox size="small" sx={{ py: 0 }}
                        checked={value.reviewers[reviewerIndex].required}
                        onChange={(_, newValue) => setFacilityLevelField(newValue, reviewerIndex, "required")}
                      />
                      <Typography variant="caption"> Review required
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )
            }
            <Grid item xs={4} >
              <Button variant="outlined" color="secondary" onClick={handleAddFacLevel}
                sx={{ borderRadius: 1, bgcolor: "#fafaff", maxWidth: 350, py: value?.reviewers?.length % 3 === 0 ? 5 : 11, px: 15.5 }}>
                Add <Add sx={{ ml: 1 }} />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="text" size="small" sx={{ ml: 1, textDecoration: 'underline' }}
            disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Tooltip title={disableSaveAllMessage}><span>
            <AwaitButton variant="contained" color="secondary" size="small" sx={{ ml: 1 }}
              loading={loading} disabled={!!disableSaveAllMessage}
              onClick={handleEditFacLevelSave} >Save
            </AwaitButton>
          </span> </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
}
